import React, { useMemo } from 'react';
import Chart from './extras/Chart';
import Card, { CardBody } from './bootstrap/Card';
import Money from '../helpers/utils/money/money';

const GraficoCmvDiario = ({ data: rows, reports }) => {
    const chartData = useMemo(() => {
        const dailyTotals = {};

        const ensureDayExists = (dayKey) => {
            if (!dailyTotals[dayKey]) {
                dailyTotals[dayKey] = {
                    automaticCostInCents: 0,
                    realCostInCents: 0,
                    totalValueInCents: 0,
                };
            }
        };

        if (rows) {
            rows.forEach(product => {
                if (product.days) {
                    Object.keys(product.days).forEach(dayKey => {
                        ensureDayExists(dayKey);
                        const dayData = product.days[dayKey];
                        const automaticCost = (Number(dayData.totalCoust) || 0) * (Number(dayData.totalQuantity) || 0);
                        const totalValue = (Number(dayData.totalValue) || 0) * (Number(dayData.totalQuantity) || 0);
                        dailyTotals[dayKey].automaticCostInCents += automaticCost;
                        dailyTotals[dayKey].totalValueInCents += totalValue;
                    });
                }
            });
        }

        if (reports) {
            reports.forEach((r) => {
                if (r.days) {
                    for (const dayKey in r.days) {
                        ensureDayExists(dayKey);
                        const day = r.days[dayKey];


                        const priceUnInCents = Number(day?.priceUn) || 0;

                        const realCost = (priceUnInCents * (Number(day?.qtdExit) || 0)) +
                            (priceUnInCents * (Number(day?.qtdLoss) || 0)) +
                            (priceUnInCents * (Number(day?.qtdPromotial) || 0));

                        dailyTotals[dayKey].realCostInCents += realCost;
                    }
                }
            });
        }

        return Object.keys(dailyTotals)
            .map(day => {
                const { automaticCostInCents, realCostInCents, totalValueInCents } = dailyTotals[day];
                const automaticCmvPercent = totalValueInCents > 0 ? (automaticCostInCents / totalValueInCents) * 100 : 0;
                const realCmvPercent = totalValueInCents > 0 ? (realCostInCents / totalValueInCents) * 100 : 0;

                return {
                    date: day,

                    automaticCostInCents: Math.round(automaticCostInCents),
                    realCostInCents: Math.round(realCostInCents),
                    automaticCmvPercent,
                    realCmvPercent,
                };
            })
            .filter(day => day.automaticCostInCents > 0 || day.realCostInCents > 0)
            .sort((a, b) => a.date.localeCompare(b.date));

    }, [rows, reports]);

    const chartOptions = {
        series: [
            { name: 'CMV Automático', data: chartData.map((item) => Money.centsToCoin(item.automaticCostInCents)) },
            { name: 'CMV Real', data: chartData.map((item) => Money.centsToCoin(item.realCostInCents)) }
        ],
        options: {
            chart: { type: 'bar', height: 350, toolbar: { show: false } },
            title: { text: 'CMV Diário: Automático vs. Real' },
            colors: ['#556ee6', '#34c38f'],
            plotOptions: { bar: { horizontal: false, columnWidth: '70%', borderRadius: 5 } },
            dataLabels: { enabled: false },
            xaxis: { categories: chartData.map((item) => item.date) },
            yaxis: {
                title: { text: 'Valor (R$)' },
                labels: {
                    formatter: (val) => {
                        if (val >= 1000) return `R$ ${(val / 1000).toFixed(1)}k`;
                        return `R$ ${val.toFixed(0)}`;
                    },
                },
            },
            tooltip: {
                theme: 'light',
                y: {
                    formatter: (value, { seriesIndex, dataPointIndex }) => {
                        const dayData = chartData[dataPointIndex];
                        if (!dayData) return value;
                        let percentValue;
                        let costValue;
                        if (seriesIndex === 0) {
                            percentValue = dayData.automaticCmvPercent;
                            costValue = dayData.automaticCostInCents;
                        } else {
                            percentValue = dayData.realCmvPercent;
                            costValue = dayData.realCostInCents;
                        }
                        const formattedPercent = `${percentValue.toFixed(2)}%`.replace('.', ',');
                        const formattedCost = Money.centsToMaskMoney(costValue);
                        return `${formattedPercent} (${formattedCost})`;
                    },
                },
            },
            legend: { position: 'top' }
        },
    };

    return (
        <Card className='mb-4 shadow-sm'>
            <CardBody>
                {chartData.length > 0 ? (
                    <Chart
                        series={chartOptions.series}
                        options={chartOptions.options}
                        type='bar'
                        height={350}
                    />
                ) : (
                    <div className='text-center p-5'>
                        <p className='lead'>Não há dados diários de CMV para exibir no período selecionado.</p>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default GraficoCmvDiario;